/**
 * Thread follow-up recommendations — feature flag.
 *
 * Unlike the summon gate (whose failure mode is SILENCE and which therefore
 * shipped default-OFF), follow-up chips are additive UI whose failure mode is
 * a visible extra row — so this ships DEFAULT ON per the 2026-08-11 owner
 * directive ("not just new threads need recommendations — all threads do").
 *
 * The env allowlist is the emergency lever, in the same CSV shape as the
 * other thread flags but with narrowing semantics: UNSET ⇒ every resolved
 * account; SET ⇒ only the listed accounts ('*' restores everyone, 'none'
 * disables the feature fleet-wide with one repo-var edit, no code change).
 * Blank/unresolved accounts stay OFF — the endpoint needs an authed account
 * anyway.
 */
export const THREAD_FOLLOWUP_RECS_ACCOUNTS_ENV = 'REACT_APP_THREAD_FOLLOWUP_RECS_ACCOUNTS';

export interface FollowupRecsEnablementContext {
  accountId?: string | null;
  /** Explicit override — wins over everything (tests / parent-computed). */
  override?: boolean;
  /** Allowlist override for tests; defaults to the build-time env value. */
  enabledAccountIds?: Iterable<string> | null;
}

/** Parse the CSV allowlist from a build-time env map (direct `process.env` read). */
export function readFollowupRecsAccountsFromEnv(
  env?: Record<string, string | undefined>,
): string[] | null {
  const raw = env
    ? env[THREAD_FOLLOWUP_RECS_ACCOUNTS_ENV]
    : process.env.REACT_APP_THREAD_FOLLOWUP_RECS_ACCOUNTS;
  if (raw === undefined || raw === null || String(raw).trim() === '') return null; // unset ⇒ default-on
  const seen = new Set<string>();
  for (const part of String(raw).split(',')) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

/**
 * Account-scoped resolver. Precedence: explicit override → env narrowing when
 * set → ON for every resolved account. Fail-closed on a blank account.
 */
export function resolveFollowupRecsEnabled(ctx: FollowupRecsEnablementContext = {}): boolean {
  if (ctx.override !== undefined) return ctx.override;

  const account = (ctx.accountId ?? '').trim();
  if (!account) return false;

  const list = ctx.enabledAccountIds !== undefined
    ? (ctx.enabledAccountIds === null ? null : [...ctx.enabledAccountIds])
    : readFollowupRecsAccountsFromEnv();
  if (list === null) return true; // unset ⇒ default-on for resolved accounts

  for (const raw of list) {
    const token = String(raw ?? '').trim();
    if (token === '*' || token === account) return true;
  }
  return false;
}
